import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

interface SearchOverlayProps {
  open: boolean;
  onClose: () => void;
}

export default function SearchOverlay({ open, onClose }: SearchOverlayProps) {
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setKeyword("");
      const timer = window.setTimeout(() => inputRef.current?.focus(), 60);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [open]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const kw = keyword.trim();
    onClose();
    navigate(kw ? `/search?q=${encodeURIComponent(kw)}` : "/search");
  };

  const hotKeywords = ["明星动态", "热点新闻", "网络热议", "社会资讯"];

  return (
    <div className="fixed inset-0 z-[80] flex items-start justify-center bg-foreground-950/45 px-4 pt-24 backdrop-blur-sm">
      <button
        type="button"
        aria-label="关闭搜索"
        onClick={onClose}
        className="absolute inset-0 h-full w-full cursor-pointer"
      />
      <div className="relative z-10 w-full max-w-2xl rounded-lg bg-background-50 p-5 md:p-7 animate-fade-up">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-base font-bold text-foreground-950">搜索资讯</h3>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-100 hover:text-foreground-900"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>
        <form onSubmit={submit} className="flex items-center gap-2">
          <div className="flex flex-1 items-center gap-2 rounded-md border border-background-200 bg-background-50 px-3 py-2.5 focus-within:border-accent-400">
            <i className="ri-search-line text-lg text-foreground-400"></i>
            <input
              ref={inputRef}
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="输入关键词，如：明星动态、新剧、夜市"
              className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
          </div>
          <button
            type="submit"
            className="whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            搜索
          </button>
        </form>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className="text-xs text-foreground-500">热门搜索：</span>
          {hotKeywords.map((kw) => (
            <button
              key={kw}
              type="button"
              onClick={() => {
                onClose();
                navigate(`/search?q=${encodeURIComponent(kw)}`);
              }}
              className="cursor-pointer rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900 transition-colors hover:bg-secondary-200"
            >
              {kw}
            </button>
          ))}
        </div>
        <p className="mt-4 text-xs text-foreground-400">
          当前为演示功能，搜索结果基于站内演示数据，暂不完全。
        </p>
      </div>
    </div>
  );
}